import React, { useMemo, useRef, useState } from "react";
import BreadCrumb from "components/common/BreadCrumb";
import ActionButtons, {
  type ActionButtonItem,
} from "components/common/ActionButtons";
import TableContainer from "components/common/TableContainer";
import { Card, CardBody, CardHeader, Container } from "reactstrap";
import { toast } from "react-toastify";
import DeleteModal from "components/common/DeleteModal";
import RoleFormModal from "features/RolesAndPermissions/RoleFormModal";

import { ColumnDef } from "@tanstack/react-table";
import { IndexColumn } from "components/common/columns";
import { RoleCode } from "features/RolesAndPermissions/constants/role.constants";
import {
  deleteRole,
  getRoles,
} from "features/RolesAndPermissions/services/RoleService";
import { Role } from "features/RolesAndPermissions/types/role.types";
import { getDocumentTitle } from "utils/helpers";
import { useAccess } from "features/Authentication/hooks/useAccess";
import { Permissions } from "features/RolesAndPermissions/constants/permission.constants";

const RoleList = () => {
  document.title = getDocumentTitle("Roles & Permissions");
  const { can } = useAccess();
  const canUpdateRoles = can(Permissions.ROLES_AND_PERMISSIONS.UPDATE);
  const canDeleteRoles = can(Permissions.ROLES_AND_PERMISSIONS.DELETE);

  const tableRef = useRef<() => void>(null);
  const [selectedRecord, setSelectedRecord] = useState<Role | null>(null);
  const [deleteModal, setDeleteModal] = useState(false);
  const [roleFormModal, setRoleFormModal] = useState(false);
  const [roleEditRecord, setRoleEditRecord] = useState<Role | null>(null);

  const openAddRoleModal = () => {
    setRoleEditRecord(null);
    setRoleFormModal(true);
  };

  const openEditRoleModal = (role: Role) => {
    setRoleEditRecord(role);
    setRoleFormModal(true);
  };

  const onClickDelete = (record: Role) => {
    setSelectedRecord(record);
    setDeleteModal(true);
  };

  const handleDelete = async () => {
    if (!selectedRecord) return;
    try {
      const response = await deleteRole(Number(selectedRecord.id));
      if (response.success) {
        toast.success(response.message);
        tableRef.current?.();
      }
      setDeleteModal(false);
    } catch (err: any) {
      toast.error(err?.response?.data?.message ?? "Failed to delete role");
    }
  };

  const columns: ColumnDef<Role>[] = useMemo(
    () => [
      IndexColumn<Role>(),
      {
        header: "Role",
        accessorKey: "name",
        enableColumnFilter: false,
        cell: ({ row }) => (
          <span className="fw-medium">{row.original.name}</span>
        ),
      },

      {
        header: "Actions",
        enableColumnFilter: false,
        cell: ({ row }) => {
          const role = row.original;
          const isSuper = role.code === RoleCode.SUPER_ADMIN;
          const showPermissions = !isSuper && canUpdateRoles;
          const showEdit = !isSuper && !role.isSystem && canUpdateRoles;
          const showDelete = !isSuper && !role.isSystem && canDeleteRoles;
          const actions: ActionButtonItem[] = [
            ...(showPermissions
              ? [
                  {
                    id: "permissions",
                    tooltip: "Manage permissions",
                    icon: "ri-settings-3-line",
                    className: "btn btn-link btn-sm p-0 text-primary",
                    as: "link" as const,
                    href: `/user-management/roles-and-permissions/${role.id}/set-permissions`,
                  },
                ]
              : []),
            ...(showEdit
              ? [
                  {
                    id: "edit",
                    tooltip: "Edit",
                    icon: "ri-pencil-fill",
                    className: "btn btn-link btn-sm p-0 text-primary",
                    as: "button" as const,
                    onClick: () => openEditRoleModal(role),
                  },
                ]
              : []),
            ...(showDelete
              ? [
                  {
                    id: "delete",
                    tooltip: "Delete",
                    icon: "ri-delete-bin-5-fill",
                    className: "btn btn-link btn-sm p-0 text-danger",
                    as: "button" as const,
                    onClick: () => onClickDelete(role),
                  },
                ]
              : []),
          ];
          if (actions.length === 0) {
            return <span className="text-muted small">—</span>;
          }
          return (
            <ActionButtons idPrefix={`role-${role.id}`} actions={actions} />
          );
        },
      },
    ],
    [canUpdateRoles, canDeleteRoles],
  );

  return (
    <React.Fragment>
      <DeleteModal
        show={deleteModal}
        onDeleteClick={handleDelete}
        onCloseClick={() => setDeleteModal(false)}
        recordId={selectedRecord ? selectedRecord.name : undefined}
      />
      <RoleFormModal
        show={roleFormModal}
        onClose={() => {
          setRoleFormModal(false);
          setRoleEditRecord(null);
        }}
        onSuccess={() => tableRef.current?.()}
        editRecord={roleEditRecord}
      />
      <div className="page-content">
        <Container fluid>
          <BreadCrumb title="Roles & Permissions" pageTitle="User Management" />
          <Card>
            <CardHeader className="d-flex flex-wrap align-items-center justify-content-between gap-2">
              <div className="d-flex align-items-center gap-2 flex-grow-1">
                <span className="avatar-sm flex-shrink-0 bg-primary-subtle text-primary rounded-circle d-flex align-items-center justify-content-center">
                  <i className="ri-shield-keyhole-line fs-18" />
                </span>
                <div>
                  <h5 className="card-title mb-0">Roles & Permissions</h5>
                  <p className="text-muted small mb-0 mt-1">
                    Manage roles and control what users can access
                  </p>
                </div>
              </div>
              {can(Permissions.ROLES_AND_PERMISSIONS.CREATE) ? (
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={openAddRoleModal}
                >
                  <i className="ri-add-line align-bottom me-1" />
                  Add Role
                </button>
              ) : null}
            </CardHeader>
            <CardBody>
              <TableContainer
                columns={columns}
                fetchApi={getRoles}
                pageSize={10}
                globalSearch={true}
                refreshRef={tableRef}
                divClass="table-responsive table-card mb-3"
                tableClass="align-middle table-nowrap mb-0"
                theadClass="table-light text-muted"
              />
            </CardBody>
          </Card>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default RoleList;
